import React from "react";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import StarIcon from "@mui/icons-material/Star";
import AddIcon from "@mui/icons-material/Add";

import LoadSpinner from "../../../utilities/LoadSpinner";
import { getRightworkAPI, postRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_check_in_template,
  API_create_check_in_template,
  API_use_default_check_in_template,
} from "../../../constants";

import CheckInTemplateEditor from "./CheckInTemplateEditor";


function CheckInTemplateSettingsDisplayConfigurations(props) {
  const { homeCompany, embedded = false } = props;

  const [template, setTemplate] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);

  const [showBlankCreate, setShowBlankCreate] = React.useState(false);
  const [blankName, setBlankName] = React.useState("Weekly 1-on-1");
  const [creating, setCreating] = React.useState(false);

  const fetchTemplate = React.useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getRightworkAPI(API_get_check_in_template, {
        company_id: homeCompany,
      });
      if (res.status === 200) {
        setTemplate(res.data?.redux?.payload?.check_in_template || null);
      }
    } catch (e) {
      setLoadError(
        e?.response?.data?.system?.message ||
          "Couldn't load template. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [homeCompany]);

  React.useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const handleUseDefault = async () => {
    setCreating(true);
    try {
      const res = await postRightworkAPI(API_use_default_check_in_template, {
        company_id: homeCompany,
      });
      if (res.status === 200) {
        setTemplate(res.data?.redux?.payload?.check_in_template || null);
      }
    } catch (e) {
      setLoadError(e?.response?.data?.system?.message || "Couldn't create template.");
    } finally {
      setCreating(false);
    }
  };

  const handleCreateBlank = async () => {
    const name = blankName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await postRightworkAPI(API_create_check_in_template, {
        company_id: homeCompany,
        name,
        cadence: "weekly",
      });
      if (res.status === 200) {
        setTemplate(res.data?.redux?.payload?.check_in_template || null);
        setShowBlankCreate(false);
      }
    } catch (e) {
      setLoadError(e?.response?.data?.system?.message || "Couldn't create template.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={embedded ? "w-full" : "max-w-5xl mx-auto p-8"}>
      {/* Header */}
      {!embedded && <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <AssignmentIndIcon className="text-violet-600" style={{ fontSize: "22px" }} />
          <h1 className="text-2xl font-semibold text-slate-900">Check-in Template</h1>
        </div>
        <p className="text-sm text-slate-500">
          Configure the structure of manager 1-on-1 check-ins across this company.
        </p>
      </div>}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadSpinner />
        </div>
      )}

      {/* Load error */}
      {!isLoading && loadError && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 mb-4">
          {loadError}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !template && (
        <div className="bg-white border border-slate-200 rounded-xl p-8">
          <h2 className="text-base font-semibold text-slate-900 mb-1">
            No template configured yet.
          </h2>
          <p className="text-sm text-slate-600 mb-6">
            Pick a starting point. You can edit either choice freely afterward.
          </p>

          <div className={embedded ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-4"}>
            {/* Use default */}
            <div className="border border-violet-200 rounded-xl p-5 bg-violet-50">
              <div className="flex items-center gap-2 mb-2">
                <StarIcon className="text-violet-600" style={{ fontSize: "18px" }} />
                <h3 className="text-sm font-semibold text-violet-900">
                  Use the default template
                </h3>
              </div>
              <p className="text-sm text-slate-700 mb-4">
                5 sections covering check-in, satisfaction, role clarity (with a
                1–10 scale), future goals, and action items. Tested with a real
                customer's 1-on-1 process.
              </p>
              <button
                onClick={handleUseDefault}
                disabled={creating}
                className="w-full bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                {creating ? "Creating…" : "Use default"}
              </button>
            </div>

            {/* Start blank */}
            <div className="border border-slate-200 rounded-xl p-5 bg-white">
              <div className="flex items-center gap-2 mb-2">
                <AddIcon className="text-slate-600" style={{ fontSize: "18px" }} />
                <h3 className="text-sm font-semibold text-slate-900">Start blank</h3>
              </div>
              <p className="text-sm text-slate-700 mb-4">
                Name your template and add sections one at a time.
              </p>

              {showBlankCreate ? (
                <div>
                  <input
                    type="text"
                    value={blankName}
                    onChange={(evt) => setBlankName(evt.target.value)}
                    placeholder="Template name"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-500"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateBlank}
                      disabled={creating || !blankName.trim()}
                      className="flex-1 bg-slate-700 hover:bg-slate-800 disabled:bg-slate-300 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors"
                    >
                      {creating ? "Creating…" : "Create"}
                    </button>
                    <button
                      onClick={() => setShowBlankCreate(false)}
                      className="text-sm font-medium text-slate-500 hover:text-slate-700 px-3"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowBlankCreate(true)}
                  className="w-full border border-slate-300 hover:border-slate-400 bg-white text-slate-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Start blank
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      {!isLoading && template && (
        <CheckInTemplateEditor
          template={template}
          homeCompany={homeCompany}
          onTemplateChange={setTemplate}
        />
      )}
    </div>
  );
}

export default CheckInTemplateSettingsDisplayConfigurations;
